import { createContext, useCallback, useContext, useMemo, useState } from "react";
import DiscussionQuestionsSheet, { type DiscussionQuestions } from "./DiscussionQuestionsSheet";

// Companion to SongPlatformsProvider: stands in for the app's Navigator, where
// FeaturedServiceView calls navigator.present(sheet: .discussionQuestions(...)).
//
// Mounted once at the root so any card can present the sheet without each page
// wiring up its own state.

const DiscussionQuestionsContext =
  createContext<((questions: DiscussionQuestions) => void) | null>(null);

export default function DiscussionQuestionsProvider(props: { children: React.ReactNode }) {
  const [questions, setQuestions] = useState<DiscussionQuestions | null>(null);

  const present = useCallback((next: DiscussionQuestions) => setQuestions(next), []);
  const dismiss = useCallback(() => setQuestions(null), []);
  const value = useMemo(() => present, [present]);

  return <DiscussionQuestionsContext.Provider value={value}>
    {props.children}
    <DiscussionQuestionsSheet questions={questions} onClose={dismiss} />
  </DiscussionQuestionsContext.Provider>
}

/**
 * Presents the discussion questions sheet.
 *
 * Returns null outside a provider so callers can fall back to their own
 * handler rather than crashing — the component gallery relies on that.
 */
export function useDiscussionQuestions(): ((questions: DiscussionQuestions) => void) | null {
  return useContext(DiscussionQuestionsContext);
}
